import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { getPresignedUrl, updateFixtureArtwork } from '../../api/tournament/fixturesService';
import { getImageUrl } from '../../utils/imageUrl';

type ArtworkFieldName = 'featuredImage';

interface ArtworkFieldProps {
  matchId: string;
  sport: string;
  field: ArtworkFieldName;
  label: string;
  description: string;
  savedKey: string | null;
  fallbackKey?: string | null;
  onSaved: () => Promise<void>;
}

const ArtworkField: React.FC<ArtworkFieldProps> = ({
  matchId, sport, field, label, description, savedKey, fallbackKey, onSaved,
}) => {
  const [pendingKey, setPendingKey] = useState<string | null>(savedKey);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPendingKey(savedKey);
    setLocalPreviewUrl(null);
  }, [matchId, sport, savedKey]);

  useEffect(() => () => {
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
  }, [localPreviewUrl]);

  const busy = uploading || saving;
  const modified = pendingKey !== savedKey;
  const previewUrl = localPreviewUrl || getImageUrl(pendingKey || fallbackKey);
  const showingFallback = !pendingKey && !!fallbackKey;

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    if (!file.type.startsWith('image/')) {
      setError('Select an image file.');
      return;
    }

    setError(null);
    setUploading(true);
    try {
      const context = `matches/${matchId}/artwork/banner-override`;
      const presign = await getPresignedUrl({ fileName: file.name, contentType: file.type, context });
      const key = presign.key || presign.fileKey;
      if (!presign.presignedUrl || !key) throw new Error('Image upload could not start.');
      const uploaded = await fetch(presign.presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!uploaded.ok) throw new Error('Image upload failed.');
      setPendingKey(key);
      setLocalPreviewUrl(URL.createObjectURL(file));
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Image upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const save = async (key: string | null) => {
    setError(null);
    setSaving(true);
    try {
      await updateFixtureArtwork(matchId, sport, field, key);
      await onSaved();
      setPendingKey(key);
      setLocalPreviewUrl(null);
    } catch (saveError: any) {
      setError(saveError?.response?.data?.error || saveError?.message || 'Could not save artwork.');
    } finally {
      setSaving(false);
    }
  };

  const remove = () => {
    if (modified) {
      setPendingKey(savedKey);
      setLocalPreviewUrl(null);
      setError(null);
    } else if (savedKey) {
      void save(null);
    }
  };

  return (
    <Col lg={6}>
      <div className="h-100 rounded border p-3">
        <h6 className="mb-1">{label}</h6>
        <p className="text-muted small mb-3">{description}</p>
        {previewUrl ? (
          <img src={previewUrl} alt={`${label} preview`} className="w-100 rounded border" style={{ aspectRatio: '16 / 9', objectFit: 'cover' }} />
        ) : (
          <div className="rounded border bg-light d-flex align-items-center justify-content-center text-muted" style={{ aspectRatio: '16 / 9' }}>
            No image configured
          </div>
        )}
        {showingFallback && <div className="small text-muted mt-1">Default artwork shown until an override is saved.</div>}
        {modified && <div className="small text-warning mt-1">Unsaved image change</div>}
        {error && <div className="alert alert-danger py-2 mt-2 mb-0" role="alert">{error}</div>}
        <div className="d-flex flex-wrap gap-2 mt-3">
          <input ref={inputRef} type="file" accept="image/*" onChange={upload} disabled={busy} hidden aria-label={`Upload ${label}`} />
          <Button size="sm" variant="outline-primary" onClick={() => inputRef.current?.click()} disabled={busy}>
            {uploading ? 'Uploading...' : pendingKey ? 'Replace image' : 'Upload image'}
          </Button>
          <Button size="sm" variant="primary" onClick={() => void save(pendingKey)} disabled={busy || !modified || !pendingKey}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button size="sm" variant="outline-danger" onClick={remove} disabled={busy || (!pendingKey && !savedKey)}>
            {modified ? 'Discard change' : 'Remove'}
          </Button>
        </div>
      </div>
    </Col>
  );
};

interface MatchArtworkSectionProps {
  matchId: string;
  sport: string;
  featuredImage?: string | null;
  backgroundImage?: string | null;
  onSaved: () => Promise<void>;
}

const MatchArtworkSection: React.FC<MatchArtworkSectionProps> = ({ matchId, sport, featuredImage, backgroundImage, onSaved }) => (
  <Card className="mb-4">
    <Card.Header><h5 className="mb-0">Match Banner Override</h5></Card.Header>
    <Card.Body>
      <p className="text-muted small">Recommended 16:9 image. Non-Football matches can override the normal category banner.</p>
      <Row className="g-3">
        <ArtworkField
          matchId={matchId}
          sport={sport}
          field="featuredImage"
          label="Banner Override"
          description="Overrides the category normal match banner where supported."
          savedKey={featuredImage || null}
          fallbackKey={backgroundImage}
          onSaved={onSaved}
        />
      </Row>
    </Card.Body>
  </Card>
);

export default MatchArtworkSection;
